// TrackingComponent.styles.js
import { makeStyles } from "@mui/styles";

export const useTrackingStyles = makeStyles((theme) => ({
  // Base styles
  container: {
    padding: "8px",
    "@media (min-width: 600px)": {
      padding: "16px",
    },
  },

  // Header styles
  flatbedHeader: {
    background: "linear-gradient(135deg, #ff7043 0%, #ff5722 100%)",
    color: "white",
    padding: "8px 16px",
    borderRadius: "12px",
    textAlign: "center",
    fontWeight: "bold",
    fontSize: "14px",
    marginBottom: "16px",
    boxShadow: "0 2px 8px rgba(255, 112, 67, 0.3)",
    "@media (min-width: 600px)": {
      fontSize: "16px",
      padding: "12px 24px",
    },
  },

  // Card styles
  infoCard: {
    borderRadius: "12px !important",
    marginBottom: "12px !important",
    boxShadow: "0 2px 12px rgba(0,0,0,0.08) !important",
    transition: "all 0.2s ease !important",
    border: "1px solid rgba(0,0,0,0.04) !important",
    "&:hover": {
      boxShadow: "0 4px 20px rgba(0,0,0,0.12) !important",
    },
  },

  infoCardContent: {
    padding: "16px !important",
    "&:last-child": {
      paddingBottom: "16px !important",
    },
  },

  // Status Timeline styles
  statusTimeline: {
    background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
    borderRadius: "12px",
    padding: "16px",
    marginBottom: "16px",
  },

  timelineHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: "16px",
    gap: "8px",
  },

  timelineStep: {
    position: "relative",
    paddingLeft: "32px",
    paddingBottom: "24px",
    "&:last-child": {
      paddingBottom: "0",
    },
    "&::before": {
      content: '""',
      position: "absolute",
      left: "11px",
      top: "24px",
      bottom: "-8px",
      width: "2px",
      backgroundColor: "#e0e0e0",
    },
    "&:last-child::before": {
      display: "none",
    },
  },

  timelineStepCompleted: {
    "&::before": {
      backgroundColor: "#4caf50",
    },
  },

  timelineIcon: {
    position: "absolute",
    left: "0",
    top: "0",
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "2px solid #e0e0e0",
    backgroundColor: "white",
    fontSize: "12px",
    fontWeight: "bold",
    color: "#666",
  },

  timelineIconCompleted: {
    backgroundColor: "#4caf50",
    borderColor: "#4caf50",
    color: "white",
  },

  timelineIconActive: {
    backgroundColor: "#2196f3",
    borderColor: "#2196f3",
    color: "white",
    animation: "$pulse 2s infinite",
  },

  "@keyframes pulse": {
    "0%": {
      transform: "scale(1)",
      boxShadow: "0 0 0 0 rgba(33, 150, 243, 0.4)",
    },
    "70%": {
      transform: "scale(1)",
      boxShadow: "0 0 0 10px rgba(33, 150, 243, 0)",
    },
    "100%": {
      transform: "scale(1)",
      boxShadow: "0 0 0 0 rgba(33, 150, 243, 0)",
    },
  },

  timelineContent: {
    backgroundColor: "white",
    borderRadius: "8px",
    padding: "12px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
    border: "1px solid rgba(0,0,0,0.05)",
  },

  timelineTitle: {
    fontWeight: "bold !important",
    fontSize: "14px !important",
    marginBottom: "4px !important",
    color: "#333 !important",
  },

  timelineTime: {
    fontSize: "12px !important",
    color: "#666 !important",
    marginBottom: "8px !important",
  },

  timelineStatus: {
    display: "inline-block",
  },

  // Accordion styles
  accordion: {
    marginBottom: "12px !important",
    borderRadius: "12px !important",
    border: "1px solid rgba(0,0,0,0.08) !important",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06) !important",
    "&:before": {
      display: "none",
    },
  },

  accordionSummary: {
    borderRadius: "12px !important",
    minHeight: "56px !important",
    "&.Mui-expanded": {
      borderBottomLeftRadius: "0 !important",
      borderBottomRightRadius: "0 !important",
    },
  },

  accordionDetails: {
    padding: "16px !important",
    borderTop: "1px solid rgba(0,0,0,0.08)",
  },

  // Picture styles
  pictureContainer: {
    textAlign: "center",
    "& .carousel-root": {
      borderRadius: "8px",
      overflow: "hidden",
    },
    "& .carousel .slide img": {
      borderRadius: "8px",
    },
  },

  picture: {
    width: "100%",
    maxWidth: "280px",
    height: "200px",
    objectFit: "cover",
    borderRadius: "8px",
    "@media (min-width: 600px)": {
      width: "150px",
      height: "300px",
      maxWidth: "none",
    },
  },

  // Notes styles
  notesList: {
    "& .MuiListItem-root": {
      borderRadius: "8px",
      marginBottom: "8px",
      backgroundColor: "#f8f9fa",
      border: "1px solid rgba(0,0,0,0.05)",
    },
  },

  notesTable: {
    "& .MuiTableHead-root": {
      backgroundColor: "#f5f5f5",
    },
    "& .MuiTableCell-head": {
      fontWeight: "bold",
      color: "#333",
    },
    "& .MuiTableRow-root:nth-of-type(even)": {
      backgroundColor: "#fafafa",
    },
  },

  // Button styles
  addButton: {
    borderRadius: "8px !important",
    textTransform: "none !important",
    fontWeight: "bold !important",
    boxShadow: "0 2px 8px rgba(25, 118, 210, 0.3) !important",
  },

  undoButton: {
    borderRadius: "6px !important",
    textTransform: "none !important",
    fontSize: "11px !important",
    padding: "4px 8px !important",
    minWidth: "auto !important",
  },

  // Dialog styles
  dialog: {
    "& .MuiDialog-paper": {
      borderRadius: "12px !important",
      "@media (max-width: 600px)": {
        margin: "16px",
        width: "calc(100% - 32px)",
        maxWidth: "none",
      },
    },
  },

  dialogTitle: {
    fontWeight: "bold !important",
    borderBottom: "1px solid rgba(0,0,0,0.08)",
  },

  // Utility styles
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "12px",
  },

  iconText: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "8px",
  },

  badge: {
    marginLeft: "8px !important",
    fontSize: "10px !important",
    height: "18px !important",
    minWidth: "18px !important",
  },

  // Status colors
  statusPending: {
    backgroundColor: "#ffa726 !important",
    color: "white !important",
  },

  statusCompleted: {
    backgroundColor: "#4caf50 !important",
    color: "white !important",
  },

  statusActive: {
    backgroundColor: "#2196f3 !important",
    color: "white !important",
  },

  // Responsive utilities
  hideOnMobile: {
    "@media (max-width: 600px)": {
      display: "none !important",
    },
  },

  hideOnDesktop: {
    "@media (min-width: 601px)": {
      display: "none !important",
    },
  },
}));
